class MouldSolveFinder extends HTMLElement {
  connectedCallback() {
    this.choices = Array.from(this.querySelectorAll('[data-ms-choice]'));
    this.results = Array.from(this.querySelectorAll('[data-ms-result]'));

    this.choices.forEach((choice) => {
      choice.addEventListener('click', () => this.showResult(choice));
    });
  }

  showResult(choice) {
    const target = choice.dataset.msChoice;

    this.choices.forEach((item) => {
      item.setAttribute('aria-pressed', String(item === choice));
    });

    this.results.forEach((result) => {
      result.hidden = result.dataset.msResult !== target;
    });

    const prompt = this.querySelector('[data-ms-prompt]');
    if (prompt) prompt.hidden = true;

    const activeResult = this.querySelector(`[data-ms-result="${target}"]`);
    activeResult?.focus({ preventScroll: true });
  }
}

class MouldSolveOfferPopup extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.ms-offer-popup__panel');
    this.closeButtons = Array.from(this.querySelectorAll('[data-ms-popup-close]'));
    this.copyButton = this.querySelector('[data-ms-popup-copy]');
    this.codeElement = this.querySelector('[data-ms-popup-code]');
    this.successState = this.querySelector('.ms-offer-popup__success');
    this.launcher = this.querySelector('[data-ms-popup-launcher]');
    this.storageKey = 'mould-solve-offer-popup';
    this.hideForCustomers = this.dataset.msPopupHideForCustomers === 'true';
    this.homeOnly = this.dataset.msPopupHomeOnly === 'true';
    this.delaySeconds = Number(this.dataset.msPopupDelay || 6);
    this.expiryDays = Number(this.dataset.msPopupExpiryDays || 14);
    this.scrollPercent = Number(this.dataset.msPopupScroll ?? 40);
    this.isSuccess = this.successState instanceof HTMLElement && !this.successState.hidden;

    if (!(this.panel instanceof HTMLElement)) return;
    if (this.hideForCustomers && document.documentElement.hasAttribute('data-customer-logged-in')) return;
    this.autoOpenAllowed = !this.homeOnly || window.location.pathname === '/';

    this.closeButtons.forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    this.launcher?.addEventListener('click', () => this.open());

    this.copyButton?.addEventListener('click', async () => {
      const code = this.codeElement?.textContent?.trim();
      if (!code) return;

      try {
        await navigator.clipboard.writeText(code);
        this.copyButton.textContent = 'Copied';
      } catch (_error) {
        this.copyButton.textContent = code;
      }
    });

    document.addEventListener('keydown', this.handleKeydown);

    if (this.isSuccess) {
      this.markSubmitted();
      this.open();
      return;
    }

    if (this.shouldSuppress()) {
      /* Already dismissed, but the offer stays reachable from the tab. */
      this.showLauncher();
      return;
    }

    if (!this.autoOpenAllowed) {
      this.showLauncher();
      return;
    }

    this.armTriggers();
  }

  armTriggers() {
    let fired = false;
    const fire = () => {
      if (fired) return;
      fired = true;
      window.removeEventListener('scroll', onScroll);
      window.clearTimeout(timer);
      this.open();
    };

    const onScroll = () => {
      if (this.scrollPercent <= 0) return;
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const pct = (window.scrollY / scrollable) * 100;
      if (pct >= this.scrollPercent) fire();
    };

    const timer = window.setTimeout(fire, Math.max(this.delaySeconds, 0) * 1000);
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  showLauncher() {
    if (this.launcher instanceof HTMLElement) this.launcher.hidden = false;
  }

  hideLauncher() {
    if (this.launcher instanceof HTMLElement) this.launcher.hidden = true;
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
  }

  handleKeydown = (event) => {
    if (event.key === 'Escape' && this.panel && !this.panel.hidden) {
      this.close();
    }
  };

  shouldSuppress() {
    try {
      const rawValue = window.localStorage.getItem(this.storageKey);
      if (!rawValue) return false;

      const parsed = JSON.parse(rawValue);
      if (parsed.status === 'submitted') return true;
      if (typeof parsed.expiresAt !== 'number') return false;

      if (Date.now() < parsed.expiresAt) return true;
      window.localStorage.removeItem(this.storageKey);
      return false;
    } catch (_error) {
      return false;
    }
  }

  open() {
    this.panel.hidden = false;
    document.body.classList.add('ms-offer-popup-open');
  }

  close() {
    this.panel.hidden = true;
    document.body.classList.remove('ms-offer-popup-open');

    if (!this.isSuccess) {
      this.setDismissed();
      /* Dismissing hides the dialog, not the offer. */
      this.showLauncher();
    }
  }

  setDismissed() {
    try {
      const expiresAt = Date.now() + this.expiryDays * 24 * 60 * 60 * 1000;
      window.localStorage.setItem(this.storageKey, JSON.stringify({ status: 'dismissed', expiresAt }));
    } catch (_error) {
      // Ignore storage failures.
    }
  }

  markSubmitted() {
    /* They have the guide. Nothing left to reopen. */
    this.hideLauncher();
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify({ status: 'submitted' }));
    } catch (_error) {
      // Ignore storage failures.
    }
  }
}

class MouldSolveAssistant extends HTMLElement {
  connectedCallback() {
    this.launcher = this.querySelector('.ms-assistant__launcher');
    this.panel = this.querySelector('.ms-assistant__panel');
    this.closeButton = this.querySelector('.ms-assistant__close');
    this.messages = this.querySelector('.ms-assistant__messages');
    this.actions = this.querySelector('.ms-assistant__actions');
    this.form = this.querySelector('.ms-assistant__form');
    this.input = this.querySelector('.ms-assistant__input');

    this.urls = {
      home: this.dataset.rootUrl,
      support: this.dataset.supportUrl,
      all: this.dataset.allUrl,
      kits: this.dataset.kitsUrl,
      sprays: this.dataset.spraysUrl,
      spore: this.dataset.sporeUrl,
      stain: this.dataset.stainUrl,
      paint: this.dataset.paintUrl,
      outdoor: this.dataset.outdoorUrl,
      advice: this.dataset.adviceUrl,
      articleRemove: this.dataset.articleRemoveUrl,
      articlePaint: this.dataset.articlePaintUrl,
      articleReturning: this.dataset.articleReturningUrl,
      articleBathroom: this.dataset.articleBathroomUrl,
      articleStain: this.dataset.articleStainUrl,
      articleOutdoor: this.dataset.articleOutdoorUrl,
      articleKits: this.dataset.articleKitsUrl,
    };

    this.assistantTitle = this.dataset.title || 'Product Help';
    this.assistantOpeningMessage =
      this.dataset.openingMessage ||
      'Tell us where the problem is or ask a question. I can point you toward the right treatment category or a safer support route.';

    if (this.panel && this.assistantTitle) {
      this.panel.setAttribute('aria-label', this.assistantTitle);
    }

    // The seven storefront categories, in Amazon order. Every route through the
    // assistant lands on one of these — no dead ends, no abstract stages.
    this.faqEntries = [
      {
        keywords: ['paint', 'painting', 'repaint', 'decorate', 'decorating', 'emulsion'],
        title: 'Should I remove mould before painting?',
        body: 'Yes. Clean the affected area, let it dry fully and deal with the moisture source. Seal any water marks with Damp & Stain Block, then finish with anti-mould paint.',
        linkLabel: 'Shop Paints',
        linkUrl: this.urls.paint,
        secondaryLinkLabel: 'Read the prep guide',
        secondaryLinkUrl: this.urls.articlePaint,
      },
      {
        keywords: ['returning', 'coming back', 'keeps returning', 'recurring', 'again', 'regrowth'],
        title: 'Why does mould keep coming back?',
        body: 'Recurring mould usually means moisture is still available through condensation, poor ventilation, a leak or another damp issue. Fog the room with Spore Stopper, but deal with the moisture cause too.',
        linkLabel: 'Shop Spore Stopper',
        linkUrl: this.urls.spore,
        secondaryLinkLabel: 'Read the recurring mould guide',
        secondaryLinkUrl: this.urls.articleReturning,
      },
      {
        keywords: ['bathroom', 'shower', 'grout', 'silicone', 'sealant', 'tiles'],
        title: 'Bathroom mould',
        body: 'On washable bathroom surfaces, start with a removal spray, then dry the area thoroughly and improve ventilation.',
        linkLabel: 'Shop Anti-Mould Sprays',
        linkUrl: this.urls.sprays,
        secondaryLinkLabel: 'Read the bathroom guide',
        secondaryLinkUrl: this.urls.articleBathroom,
      },
      {
        keywords: ['stain', 'water mark', 'watermark', 'damp patch', 'damp mark', 'yellow', 'bleed'],
        title: 'Damp marks and water stains',
        body: 'Once the moisture source is fixed and the surface is dry, seal the mark so it cannot bleed through your final coat.',
        linkLabel: 'Shop Damp & Stain Block',
        linkUrl: this.urls.stain,
        secondaryLinkLabel: 'Read the stain guide',
        secondaryLinkUrl: this.urls.articleStain,
      },
      {
        keywords: ['patio', 'driveway', 'paving', 'decking', 'outdoor', 'render', 'path', 'algae'],
        title: 'Outdoor surfaces',
        body: 'Patios, paving and driveways need a cleaner made for exterior hard surfaces affected by organic growth and weather staining.',
        linkLabel: 'Shop Driveway Cleaner',
        linkUrl: this.urls.outdoor,
        secondaryLinkLabel: 'Read the outdoor guide',
        secondaryLinkUrl: this.urls.articleOutdoor,
      },
      {
        keywords: ['kit', 'kits', 'box', 'response box', 'bundle', 'everything', 'set'],
        title: 'Do I need a kit or single products?',
        body: 'A Response Box bundles removal, protection and the safety kit in one, in the right order. Buy individually if you only need one stage.',
        linkLabel: 'Shop Response Boxes',
        linkUrl: this.urls.kits,
        secondaryLinkLabel: 'Read the kit guide',
        secondaryLinkUrl: this.urls.articleKits,
      },
      {
        keywords: ['order', 'what order', 'sequence', 'steps', 'first', 'stage'],
        title: 'What order should I use the products in?',
        body: 'Remove the visible mould, let the surface dry, block any staining, then protect with anti-mould paint. Skipping the drying stage is the most common cause of failure.',
        linkLabel: 'Shop all products',
        linkUrl: this.urls.all,
        secondaryLinkLabel: 'Browse the Advice Centre',
        secondaryLinkUrl: this.urls.advice,
      },
      {
        keywords: ['which product', 'what product', 'where do i start', 'not sure', 'help choose', 'unsure'],
        title: 'Where should I start?',
        body: 'Tell me what you can see. Visible mould starts with a spray, a damp mark starts with stain block, and a whole room is usually easiest as a Response Box.',
        linkLabel: 'Shop all products',
        linkUrl: this.urls.all,
        secondaryLinkLabel: 'Browse the Advice Centre',
        secondaryLinkUrl: this.urls.advice,
      },
    ];

    // One level, six intents. The old tree was start -> choose -> walls ->
    // walls-remove, which took four clicks to reach a product category and
    // contradicted the whole point of the redesign.
    this.flowMap = {
      start: [
        { label: 'Remove mould I can see', action: 'remove' },
        { label: 'Buy everything in one box', action: 'kits' },
        { label: 'Cover a damp mark before decorating', action: 'stain' },
        { label: 'Stop it coming back', action: 'prevent' },
        { label: 'Repaint a treated wall', action: 'paint' },
        { label: 'Clean a patio or driveway', action: 'outdoor' },
        { label: 'I still need support', action: 'support' },
      ],
    };

    this.responseMap = {
      remove: {
        title: 'Start with Anti-Mould Sprays',
        body: 'For visible mould on walls, ceilings and washable surfaces, begin with a dedicated removal spray. Let the surface dry fully before any further stage.',
        linkLabel: 'Shop Anti-Mould Sprays',
        linkUrl: this.urls.sprays,
        secondaryLinkLabel: 'Read the removal guide',
        secondaryLinkUrl: this.urls.articleRemove,
      },
      kits: {
        title: 'Response Boxes cover the whole job',
        body: 'A Response Box gives you removal, protection and the safety kit together, in the order they should be used. Essential, Advanced and Premium differ by room size and contents.',
        linkLabel: 'Shop Response Boxes',
        linkUrl: this.urls.kits,
        secondaryLinkLabel: 'Compare the kits',
        secondaryLinkUrl: this.urls.articleKits,
      },
      stain: {
        title: 'Seal it with Damp & Stain Block',
        body: 'Once the moisture source is resolved and the surface is dry, block water marks and old mould staining so they cannot bleed through your final coat.',
        linkLabel: 'Shop Damp & Stain Block',
        linkUrl: this.urls.stain,
        secondaryLinkLabel: 'Read the stain guide',
        secondaryLinkUrl: this.urls.articleStain,
      },
      prevent: {
        title: 'Spore Stopper after cleaning',
        body: 'Once the visible mould is gone and the area is dry, fog the room to help reduce regrowth. Pair it with better ventilation and humidity control.',
        linkLabel: 'Shop Spore Stopper',
        linkUrl: this.urls.spore,
        secondaryLinkLabel: 'Read the recurring mould guide',
        secondaryLinkUrl: this.urls.articleReturning,
      },
      paint: {
        title: 'Finish with anti-mould paint',
        body: 'Use anti-mould paint only after the surface has been treated, has dried fully and any moisture source has been addressed.',
        linkLabel: 'Shop Paints',
        linkUrl: this.urls.paint,
        secondaryLinkLabel: 'Read the prep guide',
        secondaryLinkUrl: this.urls.articlePaint,
      },
      outdoor: {
        title: 'Driveway Cleaner for exterior surfaces',
        body: 'For patios, paving, render and driveways, use a cleaner designed for exterior hard surfaces affected by organic growth and weather staining.',
        linkLabel: 'Shop Driveway Cleaner',
        linkUrl: this.urls.outdoor,
        secondaryLinkLabel: 'Read the outdoor guide',
        secondaryLinkUrl: this.urls.articleOutdoor,
      },
      support: {
        title: 'Support is the safest next step',
        body: 'If the surface is unusual, the mould is extensive, or you are unsure which route applies, contact Mould Solve for manual guidance. This tool gives general product guidance, not a damp survey.',
        linkLabel: 'Contact support',
        linkUrl: this.urls.support,
        secondaryLinkLabel: 'Browse the Advice Centre',
        secondaryLinkUrl: this.urls.advice,
      },
    };

    this.launcher?.addEventListener('click', () => this.toggle());
    this.closeButton?.addEventListener('click', () => this.close());
    this.form?.addEventListener('submit', (event) => this.handleSubmit(event));
    document.addEventListener('mould-solve:open-assistant', this.handleExternalOpen);

    this.reset();
  }

  disconnectedCallback() {
    document.removeEventListener('mould-solve:open-assistant', this.handleExternalOpen);
  }

  handleExternalOpen = () => {
    this.open();
  };

  reset() {
    this.messages.innerHTML = '';
    this.actions.innerHTML = '';
    this.addAssistantMessage(this.assistantOpeningMessage);
    this.renderActions('start');
  }

  toggle() {
    if (this.panel.hidden) {
      this.open();
      return;
    }
    this.close();
  }

  open() {
    this.panel.hidden = false;
    this.launcher?.setAttribute('aria-expanded', 'true');
    this.input?.focus({ preventScroll: true });
  }

  close() {
    this.panel.hidden = true;
    this.launcher?.setAttribute('aria-expanded', 'false');
  }

  addAssistantMessage(text, response) {
    const message = document.createElement('article');
    message.className = 'ms-assistant__message ms-assistant__message--assistant';

    const body = document.createElement('p');
    body.textContent = text;
    message.append(body);

    if (response?.title) {
      const title = document.createElement('h3');
      title.textContent = response.title;
      message.append(title);
    }

    if (response?.body) {
      const copy = document.createElement('p');
      copy.textContent = response.body;
      message.append(copy);
    }

    if (response?.linkLabel && response?.linkUrl) {
      const link = document.createElement('a');
      link.className = 'ms-assistant__link';
      link.href = response.linkUrl;
      link.textContent = response.linkLabel;
      message.append(link);
    }

    if (response?.secondaryLinkLabel && response?.secondaryLinkUrl) {
      const secondaryLink = document.createElement('a');
      secondaryLink.className = 'ms-assistant__link';
      secondaryLink.href = response.secondaryLinkUrl;
      secondaryLink.textContent = response.secondaryLinkLabel;
      message.append(secondaryLink);
    }

    this.messages.append(message);
    this.messages.scrollTop = this.messages.scrollHeight;
  }

  addUserMessage(text) {
    const message = document.createElement('article');
    message.className = 'ms-assistant__message ms-assistant__message--user';

    const body = document.createElement('p');
    body.textContent = text;
    message.append(body);

    this.messages.append(message);
    this.messages.scrollTop = this.messages.scrollHeight;
  }

  renderActions(key) {
    const options = this.flowMap[key] || [];
    this.actions.innerHTML = '';

    options.forEach((option) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ms-assistant__action';
      button.textContent = option.label;
      button.addEventListener('click', () => this.handleAction(option));
      this.actions.append(button);
    });

    const restart = document.createElement('button');
    restart.type = 'button';
    restart.className = 'ms-assistant__reset';
    restart.textContent = 'Start again';
    restart.addEventListener('click', () => this.reset());
    this.actions.append(restart);
  }

  handleAction(option) {
    this.open();
    this.addUserMessage(option.label);

    if (this.flowMap[option.action]) {
      const prompt = this.getPrompt(option.action);
      this.addAssistantMessage(prompt);
      this.renderActions(option.action);
      return;
    }

    const response = this.responseMap[option.action] || this.responseMap.support;
    this.addAssistantMessage('Here is the best starting point.', response);
    this.renderActions('start');
  }

  getPrompt() {
    // The flow is a single level now, so there is only one prompt to give.
    return 'Choose the closest match, or type a question below.';
  }

  handleSubmit(event) {
    event.preventDefault();

    const question = this.input?.value.trim();
    if (!question) return;

    this.open();
    this.addUserMessage(question);
    this.input.value = '';

    const response = this.findFaqMatch(question);
    if (response) {
      this.addAssistantMessage('Here’s the closest answer I can give from the current guidance.', response);
    } else {
      this.addAssistantMessage('I can help with removal sprays, Response Boxes, stain block, Spore Stopper, paint and outdoor cleaning. The Advice Centre has fuller guides.', this.responseMap.support);
    }

    this.renderActions('start');
  }

  findFaqMatch(question) {
    const query = question.toLowerCase();

    return (
      this.faqEntries.find((entry) => {
        return entry.keywords.some((keyword) => query.includes(keyword));
      }) || null
    );
  }
}

if (!customElements.get('mould-solve-finder')) {
  customElements.define('mould-solve-finder', MouldSolveFinder);
}

if (!customElements.get('mould-solve-offer-popup')) {
  customElements.define('mould-solve-offer-popup', MouldSolveOfferPopup);
}

if (!customElements.get('mould-solve-assistant')) {
  customElements.define('mould-solve-assistant', MouldSolveAssistant);
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-ms-open-assistant]');
  if (!trigger) return;

  event.preventDefault();
  document.dispatchEvent(new CustomEvent('mould-solve:open-assistant'));
});
